import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";

import { canAskUsageStats, setShareUsageStatsEnabled } from "../analytics";
import { t } from "../contract/strings";
import { Button } from "../ui";

/**
 * The one-time ask about anonymous usage stats (#1259, App Review 5.1.1(ii)).
 *
 * Shown once, the first time a meeting's notes are on screen (`ready`), and never during
 * sign-in, consent, voice setup or a meeting. It asks only while no choice is stored at
 * all (`canAskUsageStats`), and showing it stores "off" straight away, so an ask the
 * person leaves unanswered counts as asked: sharing stays off and it never comes back.
 * An answer then stores what the Settings switch stores, through the same setter.
 *
 * `forced` draws it whatever is stored: the parity gallery's `usage_ask` state, which runs
 * without an analytics key, where the real check would never ask.
 */
export function useUsageAsk(ready: boolean, forced = false): {
  shown: boolean;
  answer: (share: boolean) => void;
} {
  // Decided once, during the render where the notes first arrive (React's "adjusting state
  // when a prop changes"): `null` until then, then whether this result asks at all.
  const [asks, setAsks] = useState<boolean | null>(null);
  const [answered, setAnswered] = useState(false);
  if (ready && asks === null) setAsks(forced || canAskUsageStats());

  useEffect(() => {
    // Asked from now on, answered or not. The drawn gallery state stores nothing.
    if (asks && !forced) setShareUsageStatsEnabled(false);
  }, [asks, forced]);

  return {
    shown: asks === true && !answered,
    answer: (share) => {
      if (!forced) setShareUsageStatsEnabled(share);
      setAnswered(true);
    },
  };
}

/**
 * The ask's card: docked to the bottom of the reading column over the notes, which stay
 * readable and scrollable behind it. Not modal and no close button: the two answers are
 * its only actions, the same size side by side, Don't share (outlined) first and Share
 * (filled) second, nothing chosen in advance. The result hides Ask AI's pill while it is
 * up, since both live in the bottom corner.
 *
 * Portalled for the same reason as `Overlay`: the screen's entrance leaves a transform on
 * an ancestor, which would pin a `fixed` child to the reading column.
 */
export function UsageAsk({ onAnswer }: { onAnswer: (share: boolean) => void }) {
  const titleId = `${useId()}-usage-ask`;
  return createPortal(
    <div
      data-usage-ask
      className="fixed z-[35] inset-x-0 bottom-0 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pointer-events-none"
    >
      <div className="w-full max-w-content mx-auto px-4 pointer-events-auto">
        <section
          aria-labelledby={titleId}
          className="border rounded-card bg-surface border-line p-pad shadow-lg animate-screen-in motion-reduce:animate-none"
        >
          <strong id={titleId} className="block text-who font-semibold text-ink">
            {t("settings.stats_ask_title")}
          </strong>
          <p className="hint mt-2 mb-0">{t("settings.stats_ask_body")}</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button variant="ghost" className="w-full" onClick={() => onAnswer(false)}>
              {t("settings.stats_ask_dont_share")}
            </Button>
            <Button onClick={() => onAnswer(true)}>{t("settings.stats_ask_share")}</Button>
          </div>
        </section>
      </div>
    </div>,
    document.body,
  );
}
